import React from 'react'
import startCase from 'lodash/startCase'
// @src imports
import FontFace from 'src/editor/components/FontFace/FontFace'
import { Button, Transition } from 'src/chrome'
import { Footer } from 'src/styled'
import {
  CardType,
  getDetailedLayouts,
  getImages,
  getInfiniteDetailedLayouts,
  LayoutCategory,
  Owner,
} from 'src/graphql'
import { GlobalEditor } from '@sendoutcards/editor'

// relative imports
import EditorToolBar from '../../components/EditorToolBar/EditorToolBar'
import EditorTabs from '../EditorTabs/EditorTabs'
import EditorDrawer from '../EditorDrawer/EditorDrawer'
import ContextBar from '../../components/ContextBar/ContextBar'
import EditorFooterContents from '../../components/EditorFooterContents/EditorFooterContents'
import styles from './styles'
import { Props, Steps } from '../../types'
import apiFactory from '../../api'
import { useFlag, useMutations, useQueries, useState } from 'src/hooks'
import {
  editorFullBleed,
  editorPanel,
  newEditorCard,
  newEditorLayout,
} from '../../utils'
import { panelInput } from 'src/editor/CardUpdate'
import range from 'lodash/range'
import { isNotNullOrUndefined } from 'src/helpers'
import areEqual from 'fast-deep-equal'
import { Button as QDButton, HStack } from '@sendoutcards/quantum-design-ui'

const hasSpanView = (cardType: CardType): boolean => {
  switch (cardType) {
    case CardType.TWO_PANEL:
    case CardType.THREE_PANEL:
    case CardType.TWO_PANEL_BIG:
      return true
    default:
      return false
  }
}

const Editor: React.FC<Props> = props => {
  const api = apiFactory(props)
  const { context, saveButtonLabel, secondaryAction } = props
  const {
    step,
    isMobile,
    resolveLocation,
    card,
    fullBleeds,
    setFullBleeds,
    fonts,
    isSaving,
    panelView,
    setPanelView,
    handleSave,
    handleSaveAndSend,
  } = api

  const selectedCardType = card.type.replace(/_/g, ' ').toLowerCase()
  const hasContextBar = !!context?.lines
  const handleSecondaryAction = async () => {
    await handleSave()
    secondaryAction?.onClick()
  }
  const newSecondaryAction = secondaryAction
    ? {
        label: secondaryAction?.label,
        onClick: () => handleSecondaryAction(),
      }
    : undefined

  const [shouldShowNewCardEditor, setShouldShowNewCardEditor] = useState(true)
  const canShowNewCardEditor = useFlag('New Card Editor')
  const showNewCardEdtior = shouldShowNewCardEditor && canShowNewCardEditor
  const mutations = useMutations()
  const [images, layouts, backPanels] = useQueries(
    showNewCardEdtior ? getImages() : undefined,
    showNewCardEdtior
      ? getDetailedLayouts({
          category: LayoutCategory.BASIC_LAYOUTS,
          offset: 0,
          limit: 100,
        })
      : undefined,
    showNewCardEdtior
      ? getInfiniteDetailedLayouts({
          category: LayoutCategory.BACK_PANELS,
          owner: Owner.USER,
        })
      : undefined,
  )

  if (showNewCardEdtior && images && layouts && backPanels) {
    const allFonts = [...fonts.fonts, ...fonts.signatures]
    const newCard = newEditorCard(card, fullBleeds, allFonts)
    return (
      <>
        <GlobalEditor
          card={newCard}
          fonts={fonts.fonts}
          signatures={fonts.signatures}
          layouts={layouts.map(newEditorLayout(allFonts))}
          backPanels={{
            ...backPanels,
            results: backPanels.results.map(newEditorLayout(allFonts)),
          }}
          images={images}
          handleChangeView={() =>
            setPanelView(panelView =>
              panelView === 'fullbleed' ? 'panel' : 'fullbleed',
            )
          }
          onCreateBackPanel={(panel, layoutId) =>
            mutations.createBackPanel({
              panel: panelInput(editorPanel(panel, allFonts)),
              layoutId,
            })
          }
          onSaveBackPanel={(panel, layoutId) =>
            mutations.createOrUpdateLayout({
              panel: panelInput(editorPanel(panel, allFonts)),
              layoutId,
            })
          }
          onDeleteBackPanel={id => mutations.deleteLayout({ id })}
          onUploadImages={files =>
            range(files.length)
              .map(index => files.item(index))
              .filter(isNotNullOrUndefined)
              .forEach(image_file =>
                mutations.uploadImage({ image_file, tags: ['prompt-editor'] }),
              )
          }
          onDeleteImage={id => mutations.deleteImage({ id })}
          onSave={({ fullBleeds }) => {
            if (areEqual(fullBleeds, newCard.fullBleeds)) {
              return
            }
            setFullBleeds(fullBleeds.map(editorFullBleed(allFonts)))
          }}
          contextZones={{
            primary: (
              <HStack justify="flex-end" gap="x3">
                {context?.footerLabel(card)}
                {newSecondaryAction && (
                  <QDButton
                    type="shadow"
                    size="xSmall"
                    onClick={() => newSecondaryAction.onClick()}
                  >
                    {newSecondaryAction.label}
                  </QDButton>
                )}
                <QDButton
                  type="shadow"
                  size="xSmall"
                  disabled={isSaving}
                  onClick={() => {
                    handleSave()
                  }}
                >
                  {isSaving ? 'Saving Changes' : 'Save Changes'}
                </QDButton>
                <QDButton
                  type="primary"
                  size="xSmall"
                  onClick={() => {
                    handleSaveAndSend()
                  }}
                >
                  {saveButtonLabel}
                </QDButton>
              </HStack>
            ),
          }}
        />
        <Button
          style={{ zIndex: 1000, marginTop: 16 }}
          title={'Old Card Editor'}
          onClick={() => setShouldShowNewCardEditor(false)}
        />
      </>
    )
  }

  return (
    <div
      className="order-editor"
      css={() => styles.container(step.type !== Steps.Idle)}
    >
      <FontFace fonts={api.fonts} />
      {isSaving && <Transition message={'Saving your card...'} />}
      <div css={styles.sidebar}>
        <EditorToolBar api={api} />
      </div>
      <div css={styles.drawerContainer}>
        {step.type !== Steps.Idle && <EditorDrawer api={api} />}
      </div>
      <div id={'main'} css={styles.main}>
        {!isMobile && context?.headerText && (
          <ContextBar
            lines={context?.lines}
            title={context?.headerText}
            subtitle={context?.headerSubText}
            activeCardId={card.id}
          />
        )}
        <>
          <div css={styles.headerContainer}>
            <h4>{isMobile ? 'Card Editor' : startCase(selectedCardType)}</h4>
            <p>
              {isMobile ? startCase(selectedCardType) + ': ' : ''}{' '}
              {resolveLocation(card.type)}
            </p>
            {!isMobile && hasSpanView(card.type) && (
              <Button
                title={panelView === 'fullbleed' ? 'Panel View' : 'Span View'}
                onClick={() =>
                  setPanelView(
                    panelView === 'fullbleed' ? 'panel' : 'fullbleed',
                  )
                }
                style={{
                  position: 'absolute',
                  right: 15,
                  zIndex: 102,
                  ...(hasContextBar && { top: 90 }),
                }}
              />
            )}
          </div>
          <EditorTabs
            saveButtonLabel={saveButtonLabel}
            api={api}
            swipeableViewWidth={
              isMobile
                ? '100%'
                : step.type !== Steps.Idle
                ? 'calc(100vw - 380px)'
                : 'calc(100vw - 65px)'
            }
          />
        </>
        {!isMobile && card && (
          <Footer css={styles.footer}>
            <EditorFooterContents
              label={context?.footerLabel(card)}
              saveButtonLabel={saveButtonLabel}
              onSave={handleSaveAndSend}
              secondaryAction={newSecondaryAction}
            />
          </Footer>
        )}
      </div>
    </div>
  )
}

export default Editor
